import React, { useCallback, useEffect, useState } from "react";
import { Constants } from "@eagerdog/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
    GENERIC_POST_SHARE,
    IFeedItem,
    IFeedPostItem,
    postShareDescription,
    postShareTitle,
} from "@eagerdog/interfaces";

import { apiService } from "src/services/api.service";
import { helperService } from "src/services/helper.service";
import Button from "src/components/Button/Button";
import AchievementCard from "src/routes/Discover/Feed/FeedItem/AchievementCard";
import ClubPostCard from "src/routes/Discover/Feed/FeedItem/ClubPostCard";
import OwnerPostCard from "src/routes/Discover/Feed/FeedItem/OwnerPostCard";
import DogActivityCard from "src/routes/Discover/Feed/FeedItem/DogActivityCard";
import EventActivityCard from "src/routes/Discover/Feed/FeedItem/EventActivityCard";
import ClubActivityCard from "src/routes/Discover/Feed/FeedItem/ClubActivityCard";
import { activityLink } from "src/routes/Discover/Feed/FeedItem/ActivityBlock";
import { webOrigin } from "src/routes/Discover/Feed/FeedItem/postLinks";

import styles from "./Post.module.scss";

type LoadState = "loading" | "ready" | "not_found" | "private";

/**
 * Permalink for one feed item (/p/:id) — FEED_POST_DEEP_LINK_PRD, Decision 1.
 * Renders the same card the feed does, with the post's subject (dog, event
 * or club) beneath it so a visitor arriving from a shared link has
 * somewhere to go next. Works logged out; Like/Share/Report send the
 * viewer to log in and back here, as on the public dog page.
 */
const Post: React.FC = () => {
    const { id = "" } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [item, setItem] = useState<IFeedItem>();
    const [state, setState] = useState<LoadState>("loading");
    const loggedIn = helperService.isLoggedIn();

    const load = useCallback(() => {
        apiService.getPost(id).then((result) => {
            setItem(result);
            setState("ready");
        }).catch((e) => {
            setItem(undefined);
            setState(e?.response?.status === 403 ? "private" : "not_found");
        });
    }, [id]);

    useEffect(() => {
        window.scrollTo(0, 0);
        setState("loading");
        load();
    }, [load]);

    const pageUrl = `${webOrigin()}/p/${id}`;
    const title = item ? postShareTitle(item) : GENERIC_POST_SHARE.title;
    const description = item ? postShareDescription(item) : GENERIC_POST_SHARE.description;
    const image = item && item.type === Constants.feed_item_type.post ? permalinkImage(item) : undefined;

    // After the author deletes the post there is nothing to show here: go to
    // the post's subject when it has one, otherwise home.
    const onChange = () => {
        apiService.getPost(id).then((result) => {
            setItem(result);
        }).catch(() => {
            const subject = item && item.type === Constants.feed_item_type.post ? subjectLink(item) : undefined;
            navigate(subject || "/", { replace: true });
        });
    };

    const backLink = loggedIn
        ? <Button variant="secondary" className={styles.back} onClick={() => navigate("/")}>Back to your feed</Button>
        : <Button variant="secondary" className={styles.back} onClick={() => navigate("/")}>Back to Eager Dog</Button>;

    return (
        <div className={styles.PostPage}>
            <Helmet>
                <title>{title} | Eager Dog</title>
                <link rel="canonical" href={pageUrl} />
                <meta name="description" content={description} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={pageUrl} />
                <meta property="og:site_name" content="Eager Dog" />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                {image && <meta property="og:image" content={image} />}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={description} />
                {image && <meta name="twitter:image" content={image} />}
                {state !== "ready" && <meta name="robots" content="noindex" />}
            </Helmet>

            <div className={styles.column}>
                {state === "loading" && <div className={styles.placeholder} aria-busy="true" />}

                {state === "not_found" && (
                    <div className={styles.message}>
                        <h1>This post isn't available.</h1>
                        <p>It may have been removed, or the link is incomplete.</p>
                        <div className={styles.actions}>
                            <Link to="/" className={styles.primaryLink}>Go to Eager Dog</Link>
                        </div>
                    </div>
                )}

                {state === "private" && (
                    <div className={styles.message}>
                        <h1>This post is private.</h1>
                        <p>It's about a dog whose profile isn't public. Log in if it's yours.</p>
                        <div className={styles.actions}>
                            <Link to={`/login?r=${encodeURIComponent(`/p/${id}`)}`} className={styles.primaryLink}>Log in</Link>
                        </div>
                    </div>
                )}

                {state === "ready" && item && (
                    <>
                        {item.type === Constants.feed_item_type.achievement
                            ? <AchievementCard item={item} isPermalink />
                            : item.author_type === Constants.post_author_type.club
                                ? <ClubPostCard post={item} onChange={onChange} isPermalink />
                                : <OwnerPostCard post={item} onChange={onChange} isPermalink />}
                        {item.type === Constants.feed_item_type.post && <SubjectContext post={item} />}
                    </>
                )}

                {state !== "loading" && <div className={styles.footer}>{backLink}</div>}
            </div>
        </div>
    );
};

export default Post;

/** Where the post's subject lives, for the context card and post-delete redirect. */
const subjectLink = (post: IFeedPostItem): string | undefined => {
    const dogId = post.dog_id || post.dog_ids?.[0];
    if (dogId && post.post_type !== Constants.post_type.dog_followed) return `/dogs/${dogId}`;
    const fromActivity = activityLink(post);
    if (fromActivity) return fromActivity;
    if (post.linked_event_id) return `/e/${post.linked_event_id}`;
    if (post.author_type === Constants.post_author_type.club) return `/k/${post.author_id}`;
    return undefined;
};

/** Best image for the permalink's own og tags (crawlers get the server page instead). */
const permalinkImage = (post: IFeedPostItem): string | undefined => {
    const image = (post.media || []).find((m) => m.type === Constants.post_media_type.image);
    return image?.url || post.club?.cover_image_url || post.dog?.image_url || post.dog_image_url || undefined;
};

/**
 * One context card under the post: the dog it is about, else the event it
 * links to, else the club that wrote it. Follow posts already carry their
 * target inside the card, so they get nothing extra.
 */
const SubjectContext: React.FC<{ post: IFeedPostItem }> = ({ post }) => {
    if (post.post_type === Constants.post_type.dog_followed || post.post_type === Constants.post_type.club_followed) return null;

    const dogId = post.dog_id || post.dog_ids?.[0];
    if (dogId && post.dog) {
        return <DogActivityCard post={post} link={`/dogs/${dogId}`} />;
    }

    const isEventPost = post.post_type === Constants.post_type.event_published || post.post_type === Constants.post_type.event_registered || post.post_type === Constants.post_type.result_entered;
    if (isEventPost) {
        // The post card already renders the event card via ActivityBlock.
        return null;
    }
    if (post.linked_event_id && post.activity) {
        return <EventActivityCard post={post} link={`/e/${post.linked_event_id}`} />;
    }

    if (post.author_type === Constants.post_author_type.club && post.club) {
        return <ClubActivityCard post={post} link={`/k/${post.author_id}`} />;
    }
    return null;
};
